import React from 'react';
import { styled } from '@linaria/react';

import {
  range,
  normalize,
  clamp,
  convertCartesianToPolar,
  convertPolarToCartesian,
  convertDegreesToRadians,
} from '@/utils';
import useMousePositionWithinElement from '@/hooks/use-mouse-position-within-element';

interface Props {
  hue: number;
  setHue: React.Dispatch<React.SetStateAction<number>>;
  size: number;
  thickness?: number;
}

const HueWheel = ({ hue, setHue, size, thickness = 24 }: Props) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [isMouseDown, setIsMouseDown] = React.useState(false);

  const [mousePosition, boundingBox] = useMousePositionWithinElement(
    wrapperRef,
    { buffer: 32 }
  );

  React.useEffect(() => {
    if (
      !isMouseDown ||
      typeof mousePosition.x !== 'number' ||
      typeof mousePosition.y !== 'number' ||
      !boundingBox
    ) {
      return;
    }
    const centerPoint = [
      boundingBox.width / 2,
      boundingBox.height / 2,
    ];
    const [baseAngle] = convertCartesianToPolar(
      [mousePosition.x, mousePosition.y],
      centerPoint
    );
    const hue = Math.round((baseAngle * (180 / Math.PI) + 90) % 360);

    setHue(hue);
  }, [isMouseDown, mousePosition, boundingBox, setHue]);

  React.useEffect(() => {
    function handleMouseUp() {
      setIsMouseDown(false);
    }

    // TODO: Switch to Pointer events?
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [isMouseDown]);

  function handleKeyDown(ev: React.KeyboardEvent) {
    switch (ev.key) {
      case 'ArrowUp': {
        ev.preventDefault();

        setHue((hue: number) => {
          if (hue === 0 || hue >= 355) {
            return 0;
          }

          if (hue < 180) {
            return clamp(hue - 5, 0, 180);
          } else {
            return clamp(hue + 5, 180, 355);
          }
        });
        return;
      }
      case 'ArrowDown': {
        ev.preventDefault();

        setHue((hue) => {
          if (hue === 180) {
            return hue;
          }

          if (hue < 180) {
            return clamp(hue + 5, 0, 180);
          } else {
            return clamp(hue - 5, 180, 355);
          }
        });
        return;
      }
      case 'ArrowLeft': {
        ev.preventDefault();

        setHue((hue) => {
          if (hue === 270) {
            return hue;
          }
          if (hue < 5) {
            return hue + 355;
          }

          if (hue >= 0 && hue < 90) {
            return clamp(hue - 5, 0, 90);
          } else if (hue >= 90 && hue < 270) {
            return clamp(hue + 5, 90, 270);
          } else {
            return clamp(hue - 5, 270, 360);
          }
        });
        return;
      }
      case 'ArrowRight': {
        ev.preventDefault();

        setHue((hue) => {
          if (hue === 90) {
            return hue;
          }
          if (hue >= 355) {
            return hue - 355;
          }

          if (hue >= 0 && hue < 90) {
            return clamp(hue + 5, 0, 90);
          } else if (hue >= 90 && hue < 270) {
            return clamp(hue - 5, 90, 270);
          } else {
            return clamp(hue + 5, 270, 360);
          }
        });
        return;
      }
    }
  }

  const radius = size / 2;

  const hueInRads = convertDegreesToRadians(hue);
  const cartesian = convertPolarToCartesian([
    hueInRads,
    radius - thickness / 2 - 2,
  ]);

  const indicatorPosition = convertCartesianToReal(cartesian, radius);

  // The cartesian coordinates assume that positive values are in
  // the top-right, and that the origin is in the center
  // We need to map this to real values

  return (
    <Wrapper
      ref={wrapperRef}
      style={{
        '--radius': radius + 'px',
        '--wheel-thickness': thickness + 'px',
      }}
      onMouseDown={() => setIsMouseDown(true)}
      onTouchStart={() => setIsMouseDown(true)}
    >
      <Blocker>
        <dt>Hue</dt>
        <dd>
          {hue} degree{hue !== 1 && 's'}
        </dd>
      </Blocker>
      {indicatorPosition && (
        <Indicator
          style={{
            '--x': indicatorPosition.x + 'px',
            '--y': indicatorPosition.y + 'px',
            '--rotation': hue + 'deg',
          }}
          onKeyDown={handleKeyDown}
        />
      )}
    </Wrapper>
  );
};

function convertCartesianToReal(
  cartesian: [number, number],
  radius: number
) {
  const [y, x] = cartesian;

  const realX = normalize(x, -radius, radius, 0, radius * 2);
  const realY = normalize(y, -radius, radius, radius * 2, 0);

  return {
    x: realX,
    y: realY,
  };
}

function generateHueGradient() {
  const STEPS = 90;
  return range(STEPS)
    .map((i) => {
      const degree = normalize(i, 0, STEPS, 0, 360);
      return `hsl(${degree}deg, 100%, 50%)`;
    })
    .join(', ');
}

const Wrapper = styled.div`
  color: black;
  position: relative;
  width: calc(var(--radius) * 2);
  height: calc(var(--radius) * 2);
  display: flex;
  justify-content: center;
  align-items: center;
  background: conic-gradient(${generateHueGradient()});
  border-radius: 50%;
  cursor: pointer;
  border: var(--white-border-thickness) solid white;
  /* prettier-ignore */
  box-shadow:
    0.6px 1.3px 2.2px rgba(0, 0, 0, 0.02),
    1.5px 3.1px 5.3px rgba(0, 0, 0, 0.028),
    2.9px 5.8px 10px rgba(0, 0, 0, 0.035),
    5.1px 10.3px 17.9px rgba(0, 0, 0, 0.042),
    9.6px 19.2px 33.4px rgba(0, 0, 0, 0.05),
    23px 46px 80px rgba(0, 0, 0, 0.07);
  touch-action: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const Blocker = styled.dl`
  width: calc(100% - var(--wheel-thickness) * 2);
  height: calc(100% - var(--wheel-thickness) * 2);
  background: var(--color-background);
  border-radius: 9999px;
  display: grid;
  place-content: center;
  text-align: center;
  user-select: none;
  cursor: default;
  border: var(--white-border-thickness) solid white;
  box-shadow:
    inset 0.6px 1.3px 2.2px rgba(0, 0, 0, 0.02),
    inset 1.5px 3.1px 5.3px rgba(0, 0, 0, 0.028),
    inset 2.9px 5.8px 10px rgba(0, 0, 0, 0.035),
    inset 5.1px 10.3px 17.9px rgba(0, 0, 0, 0.042),
    inset 9.6px 19.2px 33.4px rgba(0, 0, 0, 0.05),
    inset 23px 46px 80px rgba(0, 0, 0, 0.07);

  dt {
    font-size: 0.875rem;
    text-transform: uppercase;
    color: var(--color-gray-600);
    font-weight: var(--font-weight-medium);
  }
  dd {
    font-size: 1.25rem;
    font-weight: var(--font-weight-medium);
    color: var(--color-text);
  }
`;

const Indicator = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 16px;
  height: calc(var(--wheel-thickness) + 12px);
  border: 2px solid;
  /* prettier-ignore */
  transform: translate(
    calc(-50% + var(--x) - var(--white-border-thickness)),
    calc(-50% + var(--y) - var(--white-border-thickness))
  ) rotate(var(--rotation));
  transform-origin: center center;
  box-shadow: inset 0px 0px 0px 2px white;
  background: hsl(var(--rotation) 100% 50%);
  pointer-events: none;

  &:focus {
    outline-offset: 4px;
    box-shadow:
      inset 0px 0px 0px 2px white,
      0px 0px 0px 4px white;
  }
`;

export default HueWheel;
